import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../context/auth/AuthContext';

const BatchCall = () => {
  const { isUserLoggedIn, userName, userEmail, userId } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [batchCalls, setBatchCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [formData, setFormData] = useState({
    batchCallName: '',
    fromNumber: '',
    csvFile: null,
    sendOption: 'sendNow',
    scheduleDate: '',
    scheduleTime: '',
    region: ''
  });
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCsvViewer, setShowCsvViewer] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvFileName, setCsvFileName] = useState('');
  const [loadingCsv, setLoadingCsv] = useState(false);

  // Fetch phone numbers for the "From number" dropdown
  const fetchPhoneNumbers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/phone-numbers/all`);
      const data = await response.json();
      
      if (data.success && data.phoneNumbers) {
        setPhoneNumbers(data.phoneNumbers);
      }
    } catch (error) {
      console.error('Error fetching phone numbers:', error);
    }
  };

  // Fetch batch calls from the database
  const fetchBatchCalls = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/batch-calls`);
      const data = await response.json();
      
      if (data.success && data.batchCalls) {
        setBatchCalls(data.batchCalls);
        console.log('✅ Batch calls loaded:', data.batchCalls);
      }
    } catch (error) {
      console.error('Error fetching batch calls:', error);
      setError('Failed to load batch calls');
    } finally {
      setLoading(false);
    }
  };

  // Debug authentication state
  useEffect(() => {
    console.log('🔍 Auth Debug - BatchCall Component:', {
      isUserLoggedIn,
      userName,
      userEmail,
      localStorage: localStorage.getItem('chatbotUser')
    });
  }, [isUserLoggedIn, userName, userEmail]);

  // Fetch regions from Retell API
  const fetchRegions = async () => {
    try {
      setLoadingRegions(true);
      const response = await fetch(`${API_BASE_URL}/api/regions`);
      const data = await response.json();
      
      if (data.success && data.regions) {
        setRegions(data.regions);
        console.log('✅ Regions loaded:', data.regions);
      } else {
        console.error('Failed to fetch regions:', data.error);
        // Fallback to default regions if API fails
        setRegions([
          { id: 'Australia/Sydney', name: 'Australia/Sydney' },
          { id: 'Australia/Melbourne', name: 'Australia/Melbourne' },
          { id: 'Australia/Brisbane', name: 'Australia/Brisbane' },
          { id: 'Australia/Perth', name: 'Australia/Perth' },
          { id: 'Australia/Adelaide', name: 'Australia/Adelaide' },
          { id: 'Australia/Darwin', name: 'Australia/Darwin' },
          { id: 'Australia/Hobart', name: 'Australia/Hobart' },
          { id: 'Australia/Broken_Hill', name: 'Australia/Broken_Hill' },
          { id: 'Australia/Lindeman', name: 'Australia/Lindeman' },
          { id: 'Australia/Lord_Howe', name: 'Australia/Lord_Howe' },
          { id: 'Australia/Eucla', name: 'Australia/Eucla' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      // Fallback to default regions if API fails
      setRegions([
        { id: 'Australia/Sydney', name: 'Australia/Sydney' },
        { id: 'Australia/Melbourne', name: 'Australia/Melbourne' },
        { id: 'Australia/Brisbane', name: 'Australia/Brisbane' },
        { id: 'Australia/Perth', name: 'Australia/Perth' },
        { id: 'Australia/Adelaide', name: 'Australia/Adelaide' },
        { id: 'Australia/Darwin', name: 'Australia/Darwin' },
        { id: 'Australia/Hobart', name: 'Australia/Hobart' },
        { id: 'Australia/Broken_Hill', name: 'Australia/Broken_Hill' },
        { id: 'Australia/Lindeman', name: 'Australia/Lindeman' },
        { id: 'Australia/Lord_Howe', name: 'Australia/Lord_Howe' },
        { id: 'Australia/Eucla', name: 'Australia/Eucla' }
      ]);
    } finally {
      setLoadingRegions(false);
    }
  };

  useEffect(() => {
    fetchPhoneNumbers();
    fetchBatchCalls();
    fetchRegions();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setFormData(prev => ({
        ...prev,
        csvFile: file
      }));
    } else {
      setError('Please select a valid CSV file');
    }
  };

  const toggleAccordion = (index) => {
    const newExpandedItems = new Set(expandedItems);
    if (newExpandedItems.has(index)) {
      newExpandedItems.delete(index);
    } else {
      newExpandedItems.add(index);
    }
    setExpandedItems(newExpandedItems);
  };

  const downloadTemplate = () => {
    const csvContent = 'phone_number,name,email\n+1234567890,John Doe,john@example.com\n+1987654321,Jane Smith,jane@example.com';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'batch_call_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Function to view CSV file content
  const viewCsvFile = async (batchCallName) => {
    try {
      setLoadingCsv(true);
      setCsvFileName(batchCallName);
      
      // Fetch CSV content from the secure endpoint
      const response = await fetch(`${API_BASE_URL}/api/batch-calls/download-csv/${encodeURIComponent(batchCallName)}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch CSV file');
      }
      
      const csvText = await response.text();
      
      // Parse CSV content
      const lines = csvText.split('\n').filter(line => line.trim());
      if (lines.length === 0) {
        setCsvData([]);
        setShowCsvViewer(true);
        return;
      }
      
      // Get headers from first line
      const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
      
      // Parse data rows
      const data = lines.slice(1).map(line => {
        const values = line.split(',').map(value => value.trim().replace(/"/g, ''));
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });
      
      // Transform data to swap columns and rename headers
      const transformedData = data.map(row => {
        const transformedRow = {};
        
        // Find the original columns (case-insensitive)
        const nameKey = Object.keys(row).find(key => 
          key.toLowerCase().includes('name') || key.toLowerCase() === 'name'
        );
        const phoneKey = Object.keys(row).find(key => 
          key.toLowerCase().includes('phone') || key.toLowerCase().includes('number')
        );
        
        // Swap the columns: Name first, then Phone Number
        if (nameKey && phoneKey) {
          transformedRow['Name'] = row[nameKey] || '';
          transformedRow['Phone Number'] = row[phoneKey] || '';
        } else if (nameKey) {
          transformedRow['Name'] = row[nameKey] || '';
          transformedRow['Phone Number'] = '';
        } else if (phoneKey) {
          transformedRow['Name'] = '';
          transformedRow['Phone Number'] = row[phoneKey] || '';
        } else {
          // If no standard columns found, keep original structure but rename first two columns
          const originalKeys = Object.keys(row);
          if (originalKeys.length >= 2) {
            transformedRow['Name'] = row[originalKeys[0]] || '';
            transformedRow['Phone Number'] = row[originalKeys[1]] || '';
          } else if (originalKeys.length === 1) {
            transformedRow['Name'] = row[originalKeys[0]] || '';
            transformedRow['Phone Number'] = '';
          }
        }
        
        // Add any remaining columns that aren't name or phone
        Object.keys(row).forEach(key => {
          if (key !== nameKey && key !== phoneKey) {
            transformedRow[key] = row[key];
          }
        });
        
        return transformedRow;
      });
      
      setCsvData(transformedData);
      setShowCsvViewer(true);
      
    } catch (error) {
      console.error('Error viewing CSV file:', error);
      setError('Failed to load CSV file for viewing');
    } finally {
      setLoadingCsv(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Validate user is logged in
      if (!isUserLoggedIn) {
        setError('Please log in to create batch calls');
        return;
      }

      

      // Validate required fields
      if (!formData.batchCallName || !formData.fromNumber || !formData.csvFile) {
        setError('Please fill in all required fields');
        return;
      }

             // Create FormData for file upload
       const formDataToSend = new FormData();
       formDataToSend.append('batchCallName', formData.batchCallName);
       formDataToSend.append('fromNumber', formData.fromNumber);
       formDataToSend.append('csvFile', formData.csvFile);
       formDataToSend.append('sendOption', formData.sendOption);
       
       
       // Add schedule details if scheduling is selected
       if (formData.sendOption === 'schedule') {
         formDataToSend.append('scheduleDate', formData.scheduleDate);
         formDataToSend.append('scheduleTime', formData.scheduleTime);
       }
       
       // Add user ID to the form data
       if (userId) {
         formDataToSend.append('userId', userId);
       }

             console.log('📞 Sending batch call data to backend...');
               console.log('🔍 Auth Context Debug:', {
          isUserLoggedIn: isUserLoggedIn
        });
                console.log('📋 Form data:', {
           batchCallName: formData.batchCallName,
           fromNumber: formData.fromNumber,
           fileName: formData.csvFile?.name,
           sendOption: formData.sendOption,
           
           scheduleDate: formData.sendOption === 'schedule' ? formData.scheduleDate : null,
           scheduleTime: formData.sendOption === 'schedule' ? formData.scheduleTime : null
         });

      const response = await fetch(`${API_BASE_URL}/api/batch-calls`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

                           if (data.success) {
          const scheduleInfo = formData.sendOption === 'schedule' ? 
            `\n  • Scheduled Date: ${formData.scheduleDate}
  • Scheduled Time: ${formData.scheduleTime}` : '';
          
          setSuccess(`✅ Batch call created successfully!
          
  Batch Call Details:
  • Name: ${data.data.batchCallName}
  • From Number: ${data.data.fromNumber}
  • Call Time: ${data.data.callTime}
  • Send Option: ${data.data.sendOption}${scheduleInfo}
  • CSV File: ${data.data.templateFile ? 'Uploaded to S3' : 'No file'}
  • JSON Data: ${data.data.jsonData ? 'Stored in database' : 'No data'}
  • Records Count: ${data.data.recordsCount || 0} records
  • Batch Call ID: ${data.data.retellBatchCallId || 'Not created'}
  • Tasks Count: ${data.data.tasksCount || 0} phone numbers
  • User ID: ${data.data.userId || userId || 'Not set'}
  • Phone ID: ${data.data.phoneId || 'Not set'}
  
  Your batch call has been created and ${data.data.sendOption === 'sendNow' ? 'is being processed' : 'is scheduled for later'}!
  
  CSV to JSON conversion completed with ${data.data.recordsCount || 0} records.`);
         
         setShowModal(false);
                                                                               setFormData({
             batchCallName: '',
             fromNumber: '',
             csvFile: null,
             sendOption: 'sendNow',
             scheduleDate: '',
             scheduleTime: '',
             region: ''
           });
         
         // Refresh the batch calls list
         fetchBatchCalls();
       } else {
        setError(`❌ Failed to create batch call: ${data.error}`);
        console.error('❌ Batch call creation failed:', data.error);
      }

    } catch (error) {
      console.error('Error creating batch call:', error);
      setError('Network error connecting to the server. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container d-flex flex-column h-100" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: 'calc(100vh - 80px)' }}>
      <div style={{ background: '#fff', borderRadius: '50px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', padding: '20px', minWidth: '900px', maxWidth: '1100px', width: '100%', color: '#111', marginTop: '0', marginBottom: '48px', height: '500px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className="fas fa-phone" style={{ color: '#64748b', fontSize: 22 }}></i>
            <span style={{ fontWeight: 600, fontSize: '1.25rem', color: '#374151' }}>Batch Call</span>
            {!isUserLoggedIn && (
              <span style={{ 
                background: '#fee2e2', 
                color: '#991b1b', 
                padding: '4px 8px', 
                borderRadius: '4px', 
                fontSize: '0.75rem',
                marginLeft: '10px'
              }}>
                Not logged in
              </span>
            )}
            
          </div>
          <button 
            style={{ 
              background: isUserLoggedIn ? '#111827' : '#9ca3af', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 10, 
              padding: '12px 22px', 
              fontWeight: 600, 
              fontSize: '1rem', 
              cursor: isUserLoggedIn ? 'pointer' : 'not-allowed', 
              boxShadow: '0 2px 8px rgba(102,126,234,0.12)' 
            }}
            onClick={() => isUserLoggedIn && setShowModal(true)}
            disabled={!isUserLoggedIn}
          >
            {isUserLoggedIn ? 'Create a batch call' : 'Please log in first'}
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div style={{ 
            background: '#d1fae5', 
            color: '#065f46', 
            padding: '12px 16px', 
            borderRadius: '8px', 
            marginBottom: '16px',
            border: '1px solid #a7f3d0'
          }}>
            {success}
          </div>
        )}
        
        {error && (
          <div style={{ 
            background: '#fee2e2', 
            color: '#991b1b', 
            padding: '12px 16px', 
            borderRadius: '8px', 
            marginBottom: '16px',
            border: '1px solid #fca5a5'
          }}>
            {error}
          </div>
        )}

                 {/* Batch Calls List */}
         <div style={{ flex: 1, overflowY: 'auto' }}>
           {loading ? (
             <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
               <div style={{ color: '#64748b', fontSize: '1rem' }}>Loading batch calls...</div>
             </div>
           ) : batchCalls.length === 0 ? (
             <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
               <div style={{ background: '#f8fafc', borderRadius: 12, width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                 <i className="fas fa-phone" style={{ color: '#64748b', fontSize: 26 }}></i>
               </div>
               <div style={{ color: '#222', fontWeight: 500, fontSize: '1.1rem' }}>You don't have any batch calls</div>
             </div>
           ) : (
                           <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {batchCalls.map((batchCall, index) => (
                  <div key={index} style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                  }}>
                    {/* Accordion Header */}
                    <div 
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '20px',
                        cursor: 'pointer',
                        background: expandedItems.has(index) ? '#f8fafc' : '#fff',
                        borderBottom: expandedItems.has(index) ? '1px solid #e5e7eb' : 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => toggleAccordion(index)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          background: '#f3f4f6',
                          borderRadius: '8px',
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <i className="fas fa-phone" style={{ color: '#6b7280', fontSize: '16px' }}></i>
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '1rem', color: '#111827', marginBottom: '4px' }}>
                            {batchCall.batch_call_name}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            {batchCall.phone_nickname ? `${batchCall.phone_nickname} (${batchCall.phone_number})` : batchCall.from_number}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          background: batchCall.call_time === 'Send Now' ? '#d1fae5' : 
                                    batchCall.call_time === 'Schedule' ? '#dbeafe' : '#f3f4f6',
                          color: batchCall.call_time === 'Send Now' ? '#065f46' :
                                batchCall.call_time === 'Schedule' ? '#1e40af' : '#6b7280'
                        }}>
                          {batchCall.call_time}
                        </span>
                        <i 
                          className={`fas fa-chevron-${expandedItems.has(index) ? 'up' : 'down'}`}
                          style={{ 
                            color: '#6b7280', 
                            fontSize: '14px',
                            transition: 'transform 0.2s ease',
                            transform: expandedItems.has(index) ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        ></i>
                      </div>
                    </div>
                    
                    {/* Accordion Content */}
                    {expandedItems.has(index) && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '20px',
                        fontSize: '0.875rem',
                        color: '#374151',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                           <div>
                             <span style={{ fontWeight: '500', color: '#6b7280' }}>CSV File:</span>
                             <div style={{ marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                               {batchCall.template_file ? (
                                 <>
                                   <button
                                     onClick={() => viewCsvFile(batchCall.batch_call_name)}
                                     disabled={loadingCsv}
                                     style={{
                                       background: '#3b82f6',
                                       color: '#fff',
                                       border: 'none',
                                       borderRadius: '4px',
                                       padding: '4px 8px',
                                       fontSize: '12px',
                                       cursor: loadingCsv ? 'not-allowed' : 'pointer',
                                       opacity: loadingCsv ? 0.6 : 1,
                                       display: 'flex',
                                       alignItems: 'center',
                                       gap: '4px'
                                     }}
                                   >
                                     {loadingCsv ? (
                                       <>
                                         <i className="fas fa-spinner fa-spin" style={{ fontSize: '10px' }}></i>
                                         Loading...
                                       </>
                                     ) : (
                                       <>
                                         <i className="fas fa-eye" style={{ fontSize: '10px' }}></i>
                                         View
                                       </>
                                     )}
                                   </button>
                                   <a 
                                     href={`${API_BASE_URL}/api/batch-calls/download-csv/${encodeURIComponent(batchCall.batch_call_name)}`}
                                     target="_blank" 
                                     rel="noopener noreferrer"
                                     style={{ 
                                       color: '#059669', 
                                       textDecoration: 'none',
                                       background: '#d1fae5',
                                       border: '1px solid #a7f3d0',
                                       borderRadius: '4px',
                                       padding: '4px 8px',
                                       fontSize: '12px',
                                       display: 'flex',
                                       alignItems: 'center',
                                       gap: '4px'
                                     }}
                                   >
                                     <i className="fas fa-download" style={{ fontSize: '10px' }}></i>
                                     Download
                                   </a>
                                 </>
                               ) : (
                                 'No file uploaded'
                               )}
                             </div>
                           </div>
                           <div>
                             <span style={{ fontWeight: '500', color: '#6b7280' }}>Batch Call ID:</span>
                             <div style={{ marginTop: '4px', fontSize: '0.75rem' }}>
                               {batchCall.retell_batch_call_id ? (
                                 <span style={{ color: '#059669' }}>
                                   ✓ Created (ID: {batchCall.retell_batch_call_id})
                                 </span>
                               ) : (
                                 <span style={{ color: '#6b7280' }}>
                                   Not created
                                 </span>
                               )}
                             </div>
                           </div>
                           {batchCall.call_time === 'Schedule' && batchCall.call_scheduled_datetime && (
                             <div>
                               <span style={{ fontWeight: '500', color: '#6b7280' }}>Scheduled Date & Time:</span>
                               <div style={{ marginTop: '4px' }}>
                                 {new Date(batchCall.call_scheduled_datetime).toLocaleString()}
                               </div>
                             </div>
                           )}
                         </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
           )}
         </div>
      </div>

      {/* Batch Call Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '32px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '32px'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#111827'
              }}>
                Create a batch call
              </h3>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px'
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {/* User ID Indicator */}
              {/* {userId && (
                <div style={{ 
                  background: '#f0f9ff', 
                  border: '1px solid #0ea5e9', 
                  borderRadius: '6px', 
                  padding: '8px 12px', 
                  marginBottom: '16px',
                  fontSize: '0.875rem',
                  color: '#0369a1'
                }}>
                  <i className="fas fa-user" style={{ marginRight: '6px' }}></i>
                  Creating batch call for User ID: <strong>{userId}</strong>
                </div>
              )} */}
              
              {/* Batch Call Name */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: '#111827',
                  fontSize: '0.875rem'
                }}>
                  Batch Call Name
                </label>
                <input
                  type="text"
                  value={formData.batchCallName}
                  onChange={(e) => handleInputChange('batchCallName', e.target.value)}
                  placeholder="Enter"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* From number */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: '#111827',
                  fontSize: '0.875rem'
                }}>
                  From number
                </label>
                                 <select
                   value={formData.fromNumber}
                   onChange={(e) => handleInputChange('fromNumber', e.target.value)}
                   required
                   style={{
                     width: '100%',
                     padding: '12px 16px',
                     border: '1px solid #d1d5db',
                     borderRadius: '6px',
                     fontSize: '0.875rem',
                     boxSizing: 'border-box',
                     background: '#fff'
                   }}
                 >
                   <option value="">Select a phone number</option>
                   {phoneNumbers.map((phone) => (
                     <option key={phone.id} value={phone.id}>
                       {phone.nickname} ({phone.id})
                     </option>
                   ))}
                 </select>
              </div>

              {/* Upload Recipients */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: '#111827',
                  fontSize: '0.875rem'
                }}>
                  Upload Recipients
                </label>
                
                {/* Download Template Button */}
                <button
                  type="button"
                  onClick={downloadTemplate}
                  style={{
                    background: '#f9fafb',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <i className="fas fa-download"></i>
                  Download the template
                </button>

                {/* File Upload Area */}
                <div style={{
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  padding: '32px',
                  textAlign: 'center',
                  background: '#f9fafb',
                  cursor: 'pointer',
                  position: 'relative'
                }}>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer'
                    }}
                  />
                  <i className="fas fa-cloud-upload-alt" style={{ fontSize: '2rem', color: '#6b7280', marginBottom: '12px' }}></i>
                  <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
                    Choose a csv or drag & drop it here.
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '4px' }}>
                    Up to 50 MB
                  </div>
                  {formData.csvFile && (
                    <div style={{ marginTop: '8px', color: '#059669', fontSize: '0.875rem' }}>
                      ✓ {formData.csvFile.name} selected
                    </div>
                  )}
                </div>
              </div>

                                                           {/* When to send the calls */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    display: 'block',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: '#111827',
                    fontSize: '0.875rem'
                  }}>
                    When to send the calls
                  </label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="button"
                      onClick={() => handleInputChange('sendOption', 'sendNow')}
                      style={{
                        padding: '10px 20px',
                        border: formData.sendOption === 'sendNow' ? '2px solid #2563eb' : '2px solid #d1d5db',
                        borderRadius: '8px',
                        background: formData.sendOption === 'sendNow' ? '#eff6ff' : '#fff',
                        color: formData.sendOption === 'sendNow' ? '#2563eb' : '#374151',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Send Now
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputChange('sendOption', 'schedule')}
                      style={{
                        padding: '10px 20px',
                        border: formData.sendOption === 'schedule' ? '2px solid #2563eb' : '2px solid #d1d5db',
                        borderRadius: '8px',
                        background: formData.sendOption === 'schedule' ? '#eff6ff' : '#fff',
                        color: formData.sendOption === 'schedule' ? '#2563eb' : '#374151',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Schedule
                    </button>
                  </div>
                  
                  {/* Schedule Options */}
                  {formData.sendOption === 'schedule' && (
                    <div style={{ marginTop: '16px', padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                             {/* Date and Time */}
                       <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                         {/* Date */}
                         <div>
                           <label style={{
                             display: 'block',
                             fontWeight: '500',
                             marginBottom: '8px',
                             color: '#374151',
                             fontSize: '0.875rem'
                           }}>
                             Date
                           </label>
                           <input
                             type="date"
                             value={formData.scheduleDate}
                             onChange={(e) => handleInputChange('scheduleDate', e.target.value)}
                             style={{
                               width: '100%',
                               padding: '8px 12px',
                               border: '1px solid #d1d5db',
                               borderRadius: '6px',
                               fontSize: '0.875rem',
                               boxSizing: 'border-box'
                             }}
                             onClick={(e) => e.target.showPicker()}
                           />
                         </div>
                         
                         {/* Time */}
                         <div>
                           <label style={{
                             display: 'block',
                             fontWeight: '500',
                             marginBottom: '8px',
                             color: '#374151',
                             fontSize: '0.875rem'
                           }}>
                             Time
                           </label>
                           <input
                             type="time"
                             value={formData.scheduleTime}
                             onChange={(e) => handleInputChange('scheduleTime', e.target.value)}
                             style={{
                               width: '100%',
                               padding: '8px 12px',
                               border: '1px solid #d1d5db',
                               borderRadius: '6px',
                               fontSize: '0.875rem',
                               boxSizing: 'border-box',
                               background: '#fff',
                               cursor: 'pointer'
                             }}
                             onClick={(e) => e.target.showPicker()}
                           />
                         </div>
                       </div>
                    </div>
                  )}
                </div>

              {/* Submit Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: '#fff',
                    color: '#374151',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '6px',
                    background: submitting ? '#9ca3af' : '#2563eb',
                    color: '#fff',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submitting ? 'Creating...' : 'Create Batch Call'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Viewer Modal */}
      {showCsvViewer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '90vw',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <i className="fas fa-file-csv" style={{ 
                  color: '#059669', 
                  fontSize: '20px' 
                }}></i>
                <h2 style={{
                  color: '#1f2937',
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: 0
                }}>
                  CSV File Viewer - {csvFileName}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowCsvViewer(false);
                  setCsvData([]);
                  setCsvFileName('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#374151'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* CSV Content */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              background: '#f9fafb'
            }}>
              {csvData.length === 0 ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  color: '#6b7280'
                }}>
                  No data to display
                </div>
              ) : (
                <div style={{ overflow: 'auto', maxHeight: '60vh' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px',
                    background: 'white'
                  }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#f3f4f6', zIndex: 1 }}>
                      <tr>
                        {Object.keys(csvData[0] || {}).map((header, index) => (
                          <th key={index} style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '2px solid #e5e7eb',
                            borderRight: '1px solid #e5e7eb',
                            minWidth: '120px'
                          }}>
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.map((row, rowIndex) => (
                        <tr key={rowIndex} style={{
                          borderBottom: '1px solid #e5e7eb',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'white'}
                        >
                          {Object.values(row).map((value, colIndex) => (
                            <td key={colIndex} style={{
                              padding: '12px 16px',
                              borderRight: '1px solid #e5e7eb',
                              color: '#374151',
                              wordBreak: 'break-word',
                              maxWidth: '200px'
                            }}>
                              {value || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <div style={{ color: '#6b7280', fontSize: '14px' }}>
                {csvData.length} rows displayed
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setShowCsvViewer(false);
                    setCsvData([]);
                    setCsvFileName('');
                  }}
                  style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e5e7eb';
                    e.target.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f3f4f6';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                >
                  Close
                </button>
                <a
                  href={`${API_BASE_URL}/api/batch-calls/download-csv/${encodeURIComponent(csvFileName)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
                >
                  <i className="fas fa-download" style={{ fontSize: '12px' }}></i>
                  Download CSV
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchCall; 